/**
 * LandingPage - Light Theme Loading Experience
 * 
 * Eye-friendly design with:
 * - Clean white backgrounds
 * - Soft animations
 * - Muted accent colors
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Cookies from 'universal-cookie';
import WialonService from '../Network/Wialon/WialonService';
import profileAPI from '../Network/Profile/index';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@emotion/react';
import {
    setSession,
    setError,
    setAccountInfo,
} from '../reduxStore/WialonSessionReducer';
import { useTranslation } from 'react-i18next';
import WenkTagLogo from '../Components/WenkTagLogo';
import '../styles/ModernStyles.css';

const cookies = new Cookies();

// Animation keyframes
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Floating Shape Component - Light Theme
const FloatingShape = ({ size, top, left, right, bottom, delay, filled = false }) => (
    <Box
        sx={{
            position: 'absolute',
            top,
            left,
            right,
            bottom,
            width: size,
            height: size,
            borderRadius: filled ? '12px' : '50%',
            border: filled ? 'none' : '2px solid rgba(78, 205, 196, 0.3)',
            background: filled ? 'rgba(78, 205, 196, 0.05)' : 'transparent',
            opacity: 0.6,
            animation: `${float} ${6 + delay}s ease-in-out infinite`,
            animationDelay: `${delay}s`,
            transform: filled ? 'rotate(15deg)' : 'none',
            pointerEvents: 'none',
        }}
    />
);

// Progress Step Component - Light Theme
const ProgressStep = ({ step, label, currentStep, delay }) => {
    const isActive = currentStep >= step;
    const isCurrent = currentStep === step;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                opacity: isActive ? 1 : 0.4,
                transition: 'all 0.5s ease',
                animation: `${fadeInUp} 0.5s ease-out forwards`,
                animationDelay: `${delay}s`,
            }}
        >
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: `2px solid ${isActive ? '#4ECDC4' : '#CBD5E1'}`,
                    background: isActive ? '#4ECDC4' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.5s ease',
                    boxShadow: isCurrent ? '0 0 12px rgba(78, 205, 196, 0.4)' : 'none',
                }}
            >
                {isActive && (
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: '#fff',
                        }}
                    />
                )}
            </Box>
            <Typography
                sx={{
                    fontSize: 13,
                    color: isActive ? '#4ECDC4' : '#94A3B8',
                    fontFamily: 'Cairo, sans-serif',
                    fontWeight: isActive ? 600 : 400,
                    transition: 'all 0.3s ease',
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

export default function LandingPage() {
    const [t] = useTranslation('common');
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const sid = searchParams.get('sid');
    const baseUrl = searchParams.get('baseUrl');
    const authHash = searchParams.get('authHash');

    const [currentStep, setCurrentStep] = useState(1);
    const [statusMessage, setStatusMessage] = useState(t('LANDING.connectingToWialon') || 'Connecting to Wialon...');
    const [hasError, setHasError] = useState(false);

    const steps = useMemo(() => [
        { step: 1, label: t('LANDING.connect') || 'Connect' },
        { step: 2, label: t('LANDING.verify') || 'Verify' },
        { step: 3, label: t('LANDING.load') || 'Load' },
        { step: 4, label: t('LANDING.ready') || 'Ready' },
    ], [t]);

    useEffect(() => {
        const initializeSession = async () => {
            // Validate required parameters - don't call API without them
            if (!sid && !authHash) {
                setHasError(true);
                setStatusMessage(t('LANDING.missingSession') || 'Missing session parameters');

                // Redirect to sorry page after delay
                setTimeout(() => {
                    const hostUrl = cookies.get('hostUrl') || baseUrl;
                    if (hostUrl) {
                        window.location.href = hostUrl;
                    } else {
                        navigate('/sorry', { replace: true });
                    }
                }, 2500);
                return;
            }

            try {
                setCurrentStep(1);
                setStatusMessage(t('LANDING.connectingToWialon') || 'Connecting to Wialon...');

                // Store baseUrl in cookies
                if (baseUrl) {
                    cookies.set('baseUrl', baseUrl, { path: '/' });
                    cookies.set('hostUrl', baseUrl, { path: '/' });
                }

                await new Promise(resolve => setTimeout(resolve, 500));
                setCurrentStep(2);
                setStatusMessage(t('LANDING.verifyingSession') || 'Verifying Session...');

                // Verify session with Wialon
                const sessionResult = await WialonService.duplicateSession(baseUrl, sid);

                if (sessionResult?.eid) {
                    // Store basic session data first
                    cookies.set('sid', sessionResult.eid, { path: '/' });
                    cookies.set('user_name', sessionResult.user?.nm || '', { path: '/' });
                    cookies.set('user_id', sessionResult.user?.id || '', { path: '/' });
                    cookies.set('active_session', 'yes', { path: '/' });

                    dispatch(setSession({
                        sid: sessionResult.eid,
                        userId: sessionResult.user?.id,
                        userName: sessionResult.user?.nm,
                    }));

                    // Get billing account details using search_item (like old frontend)
                    let accountName = '';
                    let accountId = '';
                    const billingAccountId = sessionResult.user?.bact;

                    if (billingAccountId) {
                        try {
                            console.log('[LandingPage] Fetching billing account details for ID:', billingAccountId);
                            const accountResult = await WialonService.searchItem(
                                baseUrl,
                                sessionResult.eid,
                                billingAccountId,
                                1
                            );

                            if (accountResult?.item?.nm && accountResult?.item?.id) {
                                accountName = accountResult.item.nm;
                                accountId = accountResult.item.id;
                                console.log('[LandingPage] Billing account loaded:', accountName, accountId);
                            }
                        } catch (accountError) {
                            console.warn('[LandingPage] Failed to load billing account:', accountError);
                            // Fallback to basic info
                            accountName = sessionResult.user?.bact || '';
                            accountId = sessionResult.user?.prp?.bact_id || '';
                        }
                    }

                    // Store account info
                    cookies.set('account_name', accountName, { path: '/' });
                    cookies.set('account_id', accountId, { path: '/' });

                    dispatch(setAccountInfo({
                        accountId: accountId,
                        accountName: accountName,
                    }));

                    await new Promise(resolve => setTimeout(resolve, 500));
                    setCurrentStep(3);
                    setStatusMessage(t('LANDING.loadingData') || 'Loading Data...');

                    // Call Profile API to validate/create user in backend
                    try {
                        const profileParams = {
                            username: sessionResult.user?.nm || '',
                            user_id: sessionResult.user?.id || '',
                            account_name: accountName,
                            account_id: accountId,
                            sid: sessionResult.eid,
                            baseurl: baseUrl,
                        };
                        await profileAPI.Profile({ params: profileParams });
                        console.log('User profile validated/created in backend');
                    } catch (profileError) {
                        console.warn('Profile check failed (non-critical):', profileError);
                        // Continue anyway - profile check is for initial setup
                    }

                    await new Promise(resolve => setTimeout(resolve, 500));
                    setCurrentStep(4);
                    setStatusMessage(t('LANDING.ready') || 'Ready!');

                    await new Promise(resolve => setTimeout(resolve, 400));
                    navigate('/system/welcome', { replace: true });
                } else {
                    throw new Error('Session verification failed');
                }
            } catch (error) {
                console.error('Session initialization failed:', error);
                setHasError(true);
                setStatusMessage(t('LANDING.authenticationFailed') || 'Authentication Failed');
                dispatch(setError(error.message || 'Session initialization failed'));

                setTimeout(() => {
                    const hostUrl = cookies.get('hostUrl') || baseUrl;
                    if (hostUrl) {
                        window.location.href = hostUrl;
                    } else {
                        navigate('/sorry', { replace: true });
                    }
                }, 2500);
            }
        };

        initializeSession();
    }, [sid, baseUrl, authHash, dispatch, navigate, t]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 50%, #E2E8F0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Floating Background Shapes */}
            <FloatingShape size={100} top="10%" left="10%" delay={0} />
            <FloatingShape size={60} top="20%" right="15%" delay={1.5} />
            <FloatingShape size={80} bottom="15%" left="8%" delay={2} />
            <FloatingShape size={50} bottom="20%" right="10%" delay={0.5} />
            <FloatingShape size={70} top="50%" left="5%" delay={1} filled />
            <FloatingShape size={55} top="60%" right="8%" delay={2.5} filled />

            {/* Main Loading Card */}
            <Box
                sx={{
                    width: '90%',
                    maxWidth: 420,
                    background: '#FFFFFF',
                    borderRadius: 4,
                    padding: 5,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.08)',
                    position: 'relative',
                    zIndex: 1,
                    animation: `${fadeInUp} 0.8s ease-out forwards`,
                }}
            >
                {/* Logo Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginBottom: 4,
                    }}
                >
                    <Box
                        sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1) 0%, rgba(108, 155, 207, 0.1) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 2,
                            animation: `${pulse} 3s ease-in-out infinite`,
                        }}
                    >
                        <WenkTagLogo height={60} variant="light" showText={false} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: '#1E293B',
                            fontFamily: 'Cairo, sans-serif',
                            marginTop: 2,
                        }}
                    >
                        WenkTag Portal
                    </Typography>
                </Box>

                {/* Progress Steps */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        marginBottom: 4,
                    }}
                >
                    {steps.map((step, index) => (
                        <ProgressStep
                            key={step.step}
                            step={step.step}
                            label={step.label}
                            currentStep={currentStep}
                            delay={index * 0.1}
                        />
                    ))}
                </Box>

                {/* Status Message */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        padding: 2,
                        background: hasError ? 'rgba(239, 68, 68, 0.08)' : 'rgba(78, 205, 196, 0.08)',
                        borderRadius: 2,
                        border: `1px solid ${hasError ? 'rgba(239, 68, 68, 0.2)' : 'rgba(78, 205, 196, 0.2)'}`,
                    }}
                >
                    {!hasError && (
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                border: '2px solid #E2E8F0',
                                borderTopColor: '#4ECDC4',
                                animation: `${spin} 0.8s linear infinite`,
                            }}
                        />
                    )}
                    <Typography
                        sx={{
                            fontSize: 14,
                            color: hasError ? '#EF4444' : '#64748B',
                            fontFamily: 'Cairo, sans-serif',
                        }}
                    >
                        {statusMessage}
                    </Typography>
                </Box>
            </Box>

            {/* Footer */}
            <Typography
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    color: '#94A3B8',
                    fontSize: 12,
                    fontFamily: 'Cairo, sans-serif',
                }}
            >
                WenkGPS. All rights reserved 2026 ©
            </Typography>
        </Box>
    );
}